import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { cartAPI, ordersAPI, couponsAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Tag } from 'lucide-react';

const CheckoutPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [email, setEmail] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    try {
      const response = await cartAPI.get();
      if (response.data.length === 0) {
        toast.error('Cart is empty');
        navigate('/cart');
        return;
      }
      setCartItems(response.data);
    } catch (error) {
      toast.error('Failed to load cart');
      navigate('/cart');
    } finally {
      setLoading(false);
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.account.price * item.quantity), 0);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setApplyingCoupon(true);
    try {
      const response = await couponsAPI.apply({
        code: couponCode,
        cart_total: calculateSubtotal()
      });
      
      setAppliedCoupon(response.data);
      toast.success(`Coupon applied! You saved ₹${response.data.discount_amount}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    if (appliedCoupon) {
      return appliedCoupon.new_total;
    }
    return subtotal;
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      // Create order
      const orderResponse = await ordersAPI.create({ 
        delivery_email: email,
        coupon_code: appliedCoupon ? couponCode : null
      });
      
      const { payment_url, order_id } = orderResponse.data;

      // Redirect to UPI Gateway payment page
      window.location.href = payment_url;
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  const subtotal = calculateSubtotal();
  const discount = appliedCoupon ? appliedCoupon.discount_amount : 0;
  const total = calculateTotal();

  return (
    <div className="min-h-screen bg-background" data-testid="checkout-page">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tight mb-8" data-testid="checkout-title">
            Checkout
          </h1>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Delivery Details */}
            <div>
              <div className="p-6 rounded-2xl border border-white/10 bg-white/5 mb-6">
                <h2 className="text-xl font-heading font-bold mb-6">Delivery Details</h2>
                <form onSubmit={handlePayment} className="space-y-4" data-testid="checkout-form">
                  <div>
                    <Label htmlFor="delivery-email">Delivery Email</Label>
                    <Input
                      id="delivery-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="delivery-email-input"
                      className="bg-white/5 border-white/10 focus:border-primary/50 mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Account credentials will be sent to this email
                    </p>
                  </div>
                  
                  <Button
                    type="submit"
                    className="w-full btn-primary-glow rounded-full text-base font-bold"
                    disabled={processing}
                    data-testid="pay-now-button"
                  >
                    {processing ? 'Processing...' : `Pay ₹${total}`}
                  </Button>
                </form>
              </div>

              {/* Coupon Code Section */}
              <div className="p-6 rounded-2xl border border-white/10 bg-white/5">
                <h2 className="text-xl font-heading font-bold mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-primary" />
                  Have a Coupon Code?
                </h2>
                
                {!appliedCoupon ? (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="bg-white/5 border-white/10 focus:border-primary/50"
                      data-testid="coupon-input"
                    />
                    <Button
                      onClick={handleApplyCoupon}
                      disabled={applyingCoupon}
                      className="rounded-full"
                      data-testid="apply-coupon-button"
                    >
                      {applyingCoupon ? 'Applying...' : 'Apply'}
                    </Button>
                  </div>
                ) : (
                  <div className="p-4 rounded-xl bg-accent/10 border border-accent/20">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-bold text-accent">{couponCode} Applied!</p>
                        <p className="text-sm text-muted-foreground">You saved ₹{discount}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveCoupon}
                        className="text-destructive"
                        data-testid="remove-coupon-button"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Order Summary */}
            <div>
              <div className="p-6 rounded-2xl border border-white/10 bg-white/5" data-testid="checkout-summary">
                <h2 className="text-xl font-heading font-bold mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => (
                    <div key={item.cart_item_id} className="flex justify-between" data-testid={`summary-item-${item.cart_item_id}`}>
                      <div>
                        <p className="font-medium">{item.account.title}</p>
                        <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-heading font-bold">₹{item.account.price * item.quantity}</p>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-white/10 pt-4 space-y-3">
                  <div className="flex justify-between text-muted-foreground">
                    <span>Subtotal</span>
                    <span>₹{subtotal}</span>
                  </div>
                  
                  {appliedCoupon && (
                    <div className="flex justify-between text-accent">
                      <span>Discount</span>
                      <span>-₹{discount}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between font-heading font-bold text-xl pt-3 border-t border-white/10">
                    <span>Total</span>
                    <span data-testid="checkout-total">₹{total}</span>
                  </div>
                </div>
                
                <div className="mt-6 text-sm text-muted-foreground space-y-2">
                  <p>✓ Secure payment via UPI Gateway</p>
                  <p>✓ Instant email delivery</p>
                  <p>✓ 100% verified accounts</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CheckoutPage;
